import { Divider, Stack } from "@mui/material";
import { useRef, useState } from "react";
import { FeaturePermission } from "@/__generated__/featurePermissions";
import { useUser } from "@/auth/useUser";
import { DrawerLayout } from "@/components/common/drawerLayout";
import { ProviderSection } from "@/components/common/providerSection";
import { CommunicationState } from "@/hooks/appointments/useApptCustomizedCommunication";
import { ClientForAppointmentCreation } from "@/hooks/appointments/useCreateAppointment";
import useFeatureFlags from "@/hooks/common/useFeatureFlags";
import { ClientSection } from "../../common/clientSection";
import CommunicationOverrideModal from "../communicationOverrideModal";
import EditClientCommsItem from "../createAppointmentDrawer/editClientCommsItem";
import { AppointmentServicesSection } from "./appointmentServicesSection";
import { DateTimeSection } from "./dateTimeSection";
import { DrawerStep } from "./multiProviderAppointmentDrawer";
import { useMultiProviderAppointmentDrawerContext } from "./multiProviderAppointmentDrawerContext";
import { MultiProviderAppointmentDrawerFormActions } from "./multiProviderAppointmentDrawerFormActions";
import { TelehealthSection } from "./telehealthSection";
import { useGetClientsCreditCard } from "./useMultiProviderAppointmentBookingWithPaymentLink";

type Props = {
  onSetDrawerStep: (step: DrawerStep) => void;
  onCloseDrawer: () => void;
  onCreateAppointmentSuccess: (appointmentId: string) => void;
  isDateConfirmed: boolean;
};

export const MultiProviderAppointmentDrawerForm = ({
  onSetDrawerStep,
  onCloseDrawer,
  onCreateAppointmentSuccess,
  isDateConfirmed,
}: Props) => {
  const {
    startDate,
    appointmentServices,
    client,
    availableServices,
    selectedProviders,
    availableProviders,
    isRebooking,
    isEditing,
    setClient,
    setProviders,
    setAppointmentServices,
    telehealthType,
    telehealthVideoLink,
    telehealthAdditionalDetails,
    setTelehealthType,
    setTelehealthVideoLink,
    setTelehealthAdditionalDetails,
    setIsTelehealthAppointmentDetailsValid,
    apptCommunicationState: communicationState,
    setApptCommunicationState: setCommunicationState,
    communicationStatusText,
  } = useMultiProviderAppointmentDrawerContext();

  const { overrideApptCommsV1Enabled } = useFeatureFlags();

  const [communicationOverrideModalOpen, setCommunicationOverrideModalOpen] =
    useState<boolean>(false);
  // this is used to reset the communication state if user cancels the modal
  const oldCommunicationState = useRef<CommunicationState>(communicationState);

  const hasNoAppointmentServices = appointmentServices.length === 0;

  const onEditTimeSelection = () => {
    onSetDrawerStep(DrawerStep.TIME_SELECTION);
  };

  const { hasFeaturePermission } = useUser();
  const hasCollectingCCLinkPermission: boolean = hasFeaturePermission(
    FeaturePermission.GET_CLIENT_CREDIT_CARD_LINK
  );

  const getClientsCreditCard = useGetClientsCreditCard();

  const onSelectClient = (client: ClientForAppointmentCreation) => {
    if (hasCollectingCCLinkPermission && client) {
      // Preload credit card info data for potential collect cc link copy
      getClientsCreditCard(client.id);
    }
    setClient(client);
  };

  const onCreateClient = () => {
    onSetDrawerStep(DrawerStep.CREATE_CLIENT);
  };
  const dateRange =
    isDateConfirmed && appointmentServices.length
      ? {
          start: appointmentServices[0].startTime,
          end: appointmentServices.at(-1).endTime,
        }
      : null;

  return (
    <DrawerLayout
      headerProps={{
        title: isRebooking
          ? "Rebook client"
          : isEditing
            ? "Edit client appointment"
            : "Create client appointment",
      }}
      content={
        <>
          <Stack
            gap={2.5}
            sx={{
              mb: 8,
            }}
            divider={<Divider orientation="horizontal" variant="fullWidth" />}
          >
            <ClientSection
              onSelect={onSelectClient}
              onCreate={onCreateClient}
              client={client}
              disabled={isEditing || isRebooking}
            />
            <ProviderSection
              disabled={!client}
              value={selectedProviders}
              options={availableProviders}
              onChange={setProviders}
            />
            <AppointmentServicesSection
              selectedProviders={selectedProviders}
              availableProviders={availableProviders}
              appointmentServices={appointmentServices}
              availableServices={availableServices}
              isRebooking={isRebooking}
              startDate={startDate}
              disabled={!client}
              onAppointmentServicesChange={setAppointmentServices}
            />
            <DateTimeSection
              disabled={hasNoAppointmentServices}
              onEdit={onEditTimeSelection}
              dateRange={dateRange}
            />
            {overrideApptCommsV1Enabled && (
              <EditClientCommsItem
                disabled={hasNoAppointmentServices}
                communicationStatusText={communicationStatusText}
                openModal={() => {
                  oldCommunicationState.current = communicationState;
                  setCommunicationOverrideModalOpen(true);
                }}
                showDivider={false}
              />
            )}

            <TelehealthSection
              selectedServices={appointmentServices.map(
                ({ serviceMenuItem }) => serviceMenuItem
              )}
              telehealthType={telehealthType}
              telehealthVideoLink={telehealthVideoLink}
              telehealthAdditionalDetails={telehealthAdditionalDetails}
              onChangeTelehealthType={setTelehealthType}
              onChangeTelehealthVideoLink={setTelehealthVideoLink}
              onChangeTelehealthAdditionalDetails={
                setTelehealthAdditionalDetails
              }
              onChangeIsTelehealthAppointmentDetailsValid={
                setIsTelehealthAppointmentDetailsValid
              }
            />
          </Stack>

          <CommunicationOverrideModal
            isOpen={communicationOverrideModalOpen}
            onCancel={() => {
              setCommunicationOverrideModalOpen(false);
              setCommunicationState(oldCommunicationState.current);
            }}
            clientName={`${client?.firstName} ${client?.lastName}`}
            communicationState={communicationState}
            setCommunicationState={setCommunicationState}
            onSave={() => setCommunicationOverrideModalOpen(false)}
          />
        </>
      }
      actions={
        <MultiProviderAppointmentDrawerFormActions
          onCloseDrawer={onCloseDrawer}
          onCreateAppointmentSuccess={onCreateAppointmentSuccess}
          isDateConfirmed={isDateConfirmed}
        />
      }
    />
  );
};
